import * as React from "react";
import { WrappedFieldProps } from "redux-form";
import Icon from "../icons/icon";
import classNames from "classnames";
import useShuffleIndex from "~/hooks/useShuffleIndex";

export interface IInputSelectProps {
    fieldName: string;
    label?: string;
    disabled?: boolean;
    className?: string;
    type?: string;
    required?: boolean;
    options: Array<{
        label: string;
        value: string;
    }>;
    defaultLabel?: string;
    enableInlineError?: boolean;
    forceErrorStyling?: boolean;
    excludeDefaultOption?: boolean;
    readyForSorting?: boolean;
}

const InputSelect: React.FC<IInputSelectProps & WrappedFieldProps> = props => {
    const {
        className,
        options,
        label,
        meta: { touched, error },
        defaultLabel,
        excludeDefaultOption,
        readyForSorting,
        enableInlineError,
        forceErrorStyling
    } = props;
    const DEFAULT_OPTION = "";
    const { onChange, ...input } = props.input;
    const allOptions =
        defaultLabel && !excludeDefaultOption
            ? [{ label: defaultLabel, value: DEFAULT_OPTION }, ...options]
            : options;
    const inputHasError = enableInlineError && touched && error;
    const [dropdownVisible, setDropdownVisible] = React.useState(false);
    const [currentIndex, shuffle] = useShuffleIndex(allOptions.length);
    const dropdownRef = React.useRef<HTMLDivElement>(null);
    const isAValueSelected = input.value !== DEFAULT_OPTION;
    const currentlySelectedOption =
        isAValueSelected &&
        allOptions.find(option => {
            return option.value === input.value;
        });
    const toggleDropdown = (toggleState = !dropdownVisible) => {
        if (!toggleState) {
            shuffle("reset");
        }
        setDropdownVisible(toggleState);
    };
    const rootClasses = classNames("input-wrapper", className, {
        "input-select-label--float": isAValueSelected,
        "input-select--dropdownVisible": dropdownVisible,
        "input-select--readyForSorting": readyForSorting,
        "input-wrapper--hasError": inputHasError || forceErrorStyling
    });

    return (
        <div
            className={rootClasses}
            onBlur={(event: React.FocusEvent) => {
                // Only close dropdown if focus moves to element outside component in order allow click events
                if (!dropdownRef.current?.contains((event.relatedTarget || document.activeElement) as Node)) {
                    toggleDropdown(false);
                }
            }}
            ref={dropdownRef}
        >
            {label && (
                <label className="input-select-label" htmlFor={input.name}>
                    {label}
                </label>
            )}
            <button
                id={input.name}
                className="input-select"
                onClick={() => toggleDropdown()}
                type="button"
                onKeyPress={(event: React.KeyboardEvent<HTMLButtonElement>) => {
                    // Tab
                    if (event.key !== "Tab") {
                        event.preventDefault();
                    }
                    // Enter
                    if (event.key === "Enter") {
                        if (dropdownVisible) {
                            // An option was selected
                            onChange(allOptions[currentIndex].value);
                        }
                        toggleDropdown();
                    }
                }}
                onKeyDown={(event: React.KeyboardEvent<HTMLButtonElement>) => {
                    // ScrollIntoView with options does not work in all browsers.
                    // Hence this check which means no support in IE/Edge/Safari.
                    const supportsNativeSmoothScroll = "scrollBehavior" in document.documentElement.style;

                    // Escape
                    if (event.keyCode === 27) {
                        toggleDropdown(false);
                    }
                    // ArrowDown
                    if (event.keyCode === 40) {
                        event.preventDefault();
                        if (!dropdownVisible) {
                            toggleDropdown(true);
                        } else {
                            shuffle("increment");

                            if (supportsNativeSmoothScroll) {
                                const nextIndex = (currentIndex + 1) % allOptions.length;
                                const currentElement = document.querySelector(
                                    `[data-value="${allOptions[nextIndex].value}"]`
                                );
                                currentElement?.scrollIntoView({ behavior: "smooth", block: "nearest" });
                            }
                        }
                    }
                    // ArrowUp
                    if (event.keyCode === 38) {
                        event.preventDefault();
                        if (!dropdownVisible) {
                            toggleDropdown(true);
                        } else {
                            shuffle("decrement");

                            if (supportsNativeSmoothScroll) {
                                const nextIndex =
                                    (currentIndex + (allOptions.length - 1)) % allOptions.length;
                                const currentElement = document.querySelector(
                                    `[data-value="${allOptions[nextIndex].value}"]`
                                );
                                currentElement?.scrollIntoView({ behavior: "smooth", block: "nearest" });
                            }
                        }
                    }

                    // Space (Edge browser only): In Edge 'Space' does not trigger and onClick event
                    // as it does in other browsers. Hence we need to add this specifically.
                    const isBrowserEdge = navigator.userAgent.toLowerCase().indexOf("edge") !== -1;
                    if (isBrowserEdge && event.keyCode === 32) {
                        if (dropdownVisible) {
                            // An option was selected
                            onChange(allOptions[currentIndex].value);
                        }
                        toggleDropdown();
                    }
                }}
                role="button"
            >
                {currentlySelectedOption && currentlySelectedOption.label}
            </button>
            {dropdownVisible && (
                <ul className="input-select__dropdown" id={`${input.name}-dropdown`}>
                    {allOptions.map((option, index) => {
                        const isOptionSelected = input.value === option.value;

                        return (
                            <li key={index} className="input-select__option">
                                <button
                                    className="input-select__option-button reset-button"
                                    aria-label={option.label}
                                    tabIndex={-1}
                                    type="button"
                                    data-value={option.value}
                                    onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                                        const { target } = event;
                                        const dataValue = (target as Element).getAttribute("data-value");
                                        let value;
                                        if (typeof option.value === "number") {
                                            // Make sure that the value is parsed as number
                                            // if that is what the options value is
                                            value = dataValue && parseInt(dataValue, 10);
                                        } else {
                                            value = dataValue;
                                        }
                                        toggleDropdown(false);
                                        onChange(value);
                                    }}
                                    data-active={currentIndex === index}
                                    disabled={isOptionSelected}
                                >
                                    {option.label}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
            <Icon iconId="icon_16_arrow-down" className="input-select__icon" />

            {inputHasError && <div className="input__error" dangerouslySetInnerHTML={{ __html: error }} />}
        </div>
    );
};

export default InputSelect;
